import { useState, useEffect } from 'react';
import { X, Lightbulb, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext.tsx';
import { useQueryClient } from '@tanstack/react-query';
import ModalContainer from './layout/ModalContainer.tsx';
import { FormGroup, FormLabel, FormInput, FormSelect, FormTextarea } from './ui/FormComponents';
import { showError, showSuccess } from '../lib/alerts';
import apiClient from '../api/client';

const STATUS_MAP = {
  capturadas: 'CAPTURED',
  avaliacao: 'ANALYSIS',
  pesquisa: 'REFINEMENT',
  mvp: 'BUSINESS_CASE',
  aprovadas: 'APPROVED',
  desenvolvimento: 'DEVELOPMENT',
  entregues: 'DELIVERED',
  arquivadas: 'ARCHIVED'
} as const;

const REVERSE_STATUS_MAP = {
  CAPTURED: 'capturadas',
  ANALYSIS: 'avaliacao',
  REFINEMENT: 'pesquisa',
  BUSINESS_CASE: 'mvp',
  APPROVED: 'aprovadas',
  DEVELOPMENT: 'desenvolvimento',
  DELIVERED: 'entregues',
  ARCHIVED: 'arquivadas'
} as const;

const PRIORITY_MAP = {
  low: 'LOW',
  medium: 'MEDIUM',
  high: 'HIGH',
  critical: 'CRITICAL'
} as const;

export default function EditIdeaModal({ 
  isOpen, 
  onClose, 
  onSuccess,
  idea
}: { 
  isOpen: boolean, 
  onClose: () => void, 
  onSuccess?: () => void,
  idea?: any
}) {
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    description: '',
    problem: '',
    solution: '',
    objective: '',
    benefits: '',
    outcomes: '',
    department: '',
    company: '',
    status: 'capturadas',
    priority: 'medium',
    score: '',
    roi: '',
    impact: '',
    effort: '',
    complexity: '',
    risk: '',
    estimatedTime: '',
    strategicAlignment: '',
    tags: ''
  });

  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  
  const queryClient = useQueryClient();

  useEffect(() => {
    if (idea) {
      setFormData({
        title: idea.title || '',
        category: idea.category || '',
        description: idea.description || '',
        problem: idea.problem || '',
        solution: idea.solution || '',
        objective: idea.objective || '',
        benefits: Array.isArray(idea.benefits) ? idea.benefits.join('\n') : idea.benefits || '',
        outcomes: idea.outcomes || '',
        department: idea.department || '',
        company: idea.company || '',
        status: REVERSE_STATUS_MAP[idea.status as keyof typeof REVERSE_STATUS_MAP] || 'capturadas',
        priority: idea.priority?.toLowerCase() || 'medium',
        score: idea.score?.toString() || '',
        roi: idea.roi || '',
        impact: idea.impact || '',
        effort: idea.effort || '',
        complexity: idea.complexity || '',
        risk: idea.risk || '',
        estimatedTime: idea.estimatedTime || '',
        strategicAlignment: idea.strategicAlignment || '',
        tags: Array.isArray(idea.tags) ? idea.tags.join(', ') : idea.tags || ''
      });
    }
  }, [idea, isOpen]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!formData.title.trim()) {
      setFormError('Informe o nome da ideia antes de salvar.');
      return;
    }

    if (!idea?.id) {
      setFormError('ID da ideia não encontrado.');
      return;
    }

    setLoading(true);
    setFormError(null);

    try {
      const scoreValue = formData.score === '' ? undefined : Number(formData.score);
      const payload: Record<string, any> = {
        title: formData.title.trim(),
        category: formData.category.trim() || undefined,
        description: formData.description.trim() || undefined,
        problem: formData.problem.trim() || undefined,
        solution: formData.solution.trim() || undefined,
        objective: formData.objective.trim() || undefined,
        outcomes: formData.outcomes.trim() || undefined,
        department: formData.department.trim() || undefined,
        company: formData.company.trim() || undefined,
        status: STATUS_MAP[formData.status as keyof typeof STATUS_MAP] || 'CAPTURED',
        priority: PRIORITY_MAP[formData.priority as keyof typeof PRIORITY_MAP] || 'MEDIUM',
        score: Number.isFinite(scoreValue) ? Math.min(100, Math.max(0, scoreValue as number)) : undefined,
        roi: formData.roi.trim() || undefined,
        impact: formData.impact.trim() || undefined,
        effort: formData.effort.trim() || undefined,
        complexity: formData.complexity.trim() || undefined,
        risk: formData.risk.trim() || undefined,
        estimatedTime: formData.estimatedTime.trim() || undefined,
        strategicAlignment: formData.strategicAlignment.trim() || undefined,
        tags: formData.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
      };

      const benefits = formData.benefits
        .split(/\n|,/)
        .map((item) => item.trim())
        .filter(Boolean);

      if (benefits.length) payload.benefits = benefits;

      const res = await apiClient.put(`/api/ideas/${idea.id}`, payload);

      queryClient.invalidateQueries({ queryKey: ['ideas', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['ideas', 'details', idea.id] });
      queryClient.invalidateQueries({ queryKey: ['ideas', 'dashboard', idea.id] });

      onSuccess?.();
      showSuccess('Ideia atualizada com sucesso!');
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Falha ao atualizar ideia';
      console.error(err);
      setFormError(message);
      showError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalContainer isOpen={isOpen} onClose={onClose} maxWidth="max-w-3xl">
      <div className="px-6 py-4.5 border-b border-[#0F172A05] flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
            <Lightbulb size={14} strokeWidth={2.5} />
          </div>
          <div>
            <h3 className="text-sm font-bold text-[#111111]">Editar Ideia</h3>
            <p className="text-[10px] font-medium text-[#64748B] mt-0.5">Atualize os dados da ideia e suas propriedades</p>
          </div>
        </div>
        <button 
          type="button"
          onClick={onClose}
          className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
        >
          <X size={15} strokeWidth={2.5} />
        </button>
      </div>

      <form onSubmit={handleSave} className="p-6 flex flex-col gap-5 text-left max-h-[70vh] overflow-y-auto">
        {formError && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {formError}
          </div>
        )}

        {/* ESSENCIAL - Campos básicos obrigatórios */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <div className="w-1 h-5 bg-amber-600 rounded-full"></div>
            <p className="text-xs font-black uppercase tracking-wider text-slate-600">Essencial</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup className="md:col-span-2">
              <FormLabel required>Nome da Ideia</FormLabel>
              <FormInput 
                placeholder="Ex: Plataforma de gestão de onboarding" 
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Categoria</FormLabel>
              <FormInput 
                placeholder="Ex: SaaS, IA, Operação..."
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <FormLabel>Departamento</FormLabel>
              <FormInput 
                placeholder="Ex: Produto, Vendas"
                value={formData.department}
                onChange={(e) => handleInputChange('department', e.target.value)}
              />
            </FormGroup>

            <FormGroup className="md:col-span-2">
              <FormLabel>Descrição breve</FormLabel>
              <FormTextarea 
                rows={2}
                placeholder="Descreva o contexto e o propósito em poucas linhas"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
              />
            </FormGroup>
          </div>
        </div>

        {/* CAMPOS AVANÇADOS - Inicialmente ocultos */}
        {showAdvanced && (
          <div className="flex flex-col gap-4 pt-4 border-t border-slate-200">
            <div className="flex items-center gap-2">
              <div className="w-1 h-5 bg-indigo-600 rounded-full"></div>
              <p className="text-xs font-black uppercase tracking-wider text-slate-600">Campos avançados</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormGroup>
                <FormLabel>Empresa / Cliente</FormLabel>
                <FormInput 
                  placeholder="Ex: Cyzor, Cliente A"
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Status</FormLabel>
                <FormSelect value={formData.status} onChange={(e) => handleInputChange('status', e.target.value)}>
                  <option value="capturadas">Capturada</option>
                  <option value="avaliacao">Em análise</option>
                  <option value="pesquisa">Refinamento</option>
                  <option value="mvp">Business case</option>
                  <option value="aprovadas">Aprovada</option>
                  <option value="desenvolvimento">Em desenvolvimento</option>
                  <option value="entregues">Entregue</option>
                  <option value="arquivadas">Arquivada</option>
                </FormSelect>
              </FormGroup>

              <FormGroup>
                <FormLabel>Prioridade</FormLabel>
                <FormSelect value={formData.priority} onChange={(e) => handleInputChange('priority', e.target.value)}>
                  <option value="low">Baixa</option>
                  <option value="medium">Média</option>
                  <option value="high">Alta</option>
                  <option value="critical">Crítica</option>
                </FormSelect>
              </FormGroup>

              <FormGroup>
                <FormLabel>Score de inovação</FormLabel>
                <FormInput 
                  type="number"
                  min={0}
                  max={100}
                  placeholder="0 a 100"
                  value={formData.score}
                  onChange={(e) => handleInputChange('score', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>ROI / Potencial</FormLabel>
                <FormInput 
                  placeholder="Ex: 30% de aumento"
                  value={formData.roi}
                  onChange={(e) => handleInputChange('roi', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Objetivo principal</FormLabel>
                <FormInput 
                  placeholder="Ex: reduzir churn"
                  value={formData.objective}
                  onChange={(e) => handleInputChange('objective', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Impacto esperado</FormLabel>
                <FormInput 
                  placeholder="Ex: Alto impacto"
                  value={formData.impact}
                  onChange={(e) => handleInputChange('impact', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Esforço</FormLabel>
                <FormInput 
                  placeholder="Ex: 2 semanas"
                  value={formData.effort}
                  onChange={(e) => handleInputChange('effort', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Complexidade</FormLabel>
                <FormInput 
                  placeholder="Ex: Média"
                  value={formData.complexity}
                  onChange={(e) => handleInputChange('complexity', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Risco</FormLabel>
                <FormInput 
                  placeholder="Ex: Baixo"
                  value={formData.risk}
                  onChange={(e) => handleInputChange('risk', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Tempo estimado</FormLabel>
                <FormInput 
                  placeholder="Ex: 30 dias"
                  value={formData.estimatedTime}
                  onChange={(e) => handleInputChange('estimatedTime', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Alinhamento estratégico (%)</FormLabel>
                <FormInput 
                  placeholder="Ex: 80"
                  value={formData.strategicAlignment}
                  onChange={(e) => handleInputChange('strategicAlignment', e.target.value)}
                />
              </FormGroup>

              <FormGroup className="md:col-span-2">
                <FormLabel>Problema</FormLabel>
                <FormTextarea 
                  rows={2}
                  placeholder="Qual dor ou oportunidade resolve?"
                  value={formData.problem}
                  onChange={(e) => handleInputChange('problem', e.target.value)}
                />
              </FormGroup>

              <FormGroup className="md:col-span-2">
                <FormLabel>Solução proposta</FormLabel>
                <FormTextarea 
                  rows={2}
                  placeholder="Como funciona na prática?"
                  value={formData.solution}
                  onChange={(e) => handleInputChange('solution', e.target.value)}
                />
              </FormGroup>

              <FormGroup className="md:col-span-2">
                <FormLabel>Benefícios esperados</FormLabel>
                <FormTextarea 
                  rows={2}
                  placeholder="Um benefício por linha"
                  value={formData.benefits}
                  onChange={(e) => handleInputChange('benefits', e.target.value)}
                />
              </FormGroup>

              <FormGroup className="md:col-span-2">
                <FormLabel>Resultados esperados</FormLabel>
                <FormTextarea 
                  rows={2}
                  placeholder="O que se espera ganhar?"
                  value={formData.outcomes}
                  onChange={(e) => handleInputChange('outcomes', e.target.value)}
                />
              </FormGroup>

              <FormGroup className="md:col-span-2">
                <FormLabel>Tags</FormLabel>
                <FormInput 
                  placeholder="Ex: produto, IA, onboarding"
                  value={formData.tags}
                  onChange={(e) => handleInputChange('tags', e.target.value)}
                />
              </FormGroup>
            </div>
          </div>
        )}

        {/* Toggle para mostrar/esconder campos avançados */}
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-xs font-bold text-slate-600 hover:text-slate-900 py-2 transition-colors"
        >
          {showAdvanced ? '▼' : '▶'} {showAdvanced ? 'Ocultar' : 'Mostrar'} campos avançados
        </button>

        {/* Botões de ação */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#0F172A05]">
          <button 
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
          >
            Cancelar
          </button>
          <button 
            type="submit"
            disabled={loading || !formData.title.trim()}
            className="px-5 py-2 text-xs font-bold text-white bg-[#111111] hover:bg-[#222222] rounded-xl shadow-sm hover:scale-[1.01] transition-all cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
          >
            {loading ? 'Salvando...' : (
              <>
                <Check size={14} strokeWidth={2.5} />
                <span>Salvar Alterações</span>
              </>
            )}
          </button>
        </div>
      </form>
    </ModalContainer>
  );
}
